import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faMagnifyingGlass,
} from "@fortawesome/pro-light-svg-icons";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "app/shadcnComponents/ui/popover";

export type WrapUpReportOption = {
  id: number;
  label: string;
  // Options without a group render as standalone items with no section header
  group: string | null;
};

type WrapUpReportSelectProps = {
  options: WrapUpReportOption[];
  value: number | null;
  onChange: (id: number) => void;
};

type Section = {
  group: string | null;
  options: WrapUpReportOption[];
};

export const WrapUpReportSelect = ({
  options,
  value,
  onChange,
}: WrapUpReportSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // The option the arrow keys are on. Focus stays in the search input, and screen
  // readers follow this through aria-activedescendant.
  const [activeId, setActiveId] = useState<number | null>(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const getOptionId = (id: number) => `${baseId}-option-${id}`;

  const selectedOption = options.find((option) => option.id === value);

  const sections = useMemo<Section[]>(() => {
    const search = query.trim().toLowerCase();
    const result: Section[] = [];
    options
      .filter((option) => option.label.toLowerCase().includes(search))
      .forEach((option) => {
        const last = result[result.length - 1];
        if (last && last.group === option.group) {
          last.options.push(option);
        } else {
          result.push({ group: option.group, options: [option] });
        }
      });
    return result;
  }, [options, query]);

  const visibleOptions = useMemo(
    () => sections.flatMap((section) => section.options),
    [sections]
  );

  // On open and on each search, start on the selected report if it matches, else the first match
  useEffect(() => {
    if (!open) return;
    setActiveId(
      visibleOptions.some((option) => option.id === value)
        ? value
        : visibleOptions[0]?.id ?? null
    );
  }, [open, visibleOptions, value]);

  useEffect(() => {
    if (!open || activeId === null) return;
    document
      .getElementById(getOptionId(activeId))
      ?.scrollIntoView?.({ block: "nearest" });
    // getOptionId only depends on baseId, which never changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeId]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const handleSelect = (id: number) => {
    onChange(id);
    handleOpenChange(false);
  };

  // Escape is handled by the popover, which closes and returns focus to the trigger
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (visibleOptions.length === 0) return;
    const lastIndex = visibleOptions.length - 1;
    const activeIndex = visibleOptions.findIndex(
      (option) => option.id === activeId
    );

    let nextIndex: number;
    switch (e.key) {
      case "ArrowDown":
        nextIndex = activeIndex < 0 ? 0 : Math.min(activeIndex + 1, lastIndex);
        break;
      case "ArrowUp":
        nextIndex = activeIndex < 0 ? lastIndex : Math.max(activeIndex - 1, 0);
        break;
      case "Enter":
        if (activeId !== null) {
          e.preventDefault();
          handleSelect(activeId);
        }
        return;
      default:
        return;
    }
    e.preventDefault();
    setActiveId(visibleOptions[nextIndex].id);
  };

  return (
    // The card header toggles collapse on click, and React bubbles portal events through here
    <Box onClick={(e) => e.stopPropagation()}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Box
            component="button"
            type="button"
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: ".5rem",
              height: "2.5rem",
              maxWidth: "20rem",
              padding: "0 .75rem",
              boxSizing: "border-box",
              borderRadius: ".75rem",
              background: "white",
              border: `1px solid ${
                open
                  ? "var(--team-app-interactive-active)"
                  : "var(--team-app-filter-active-border)"
              }`,
              cursor: "pointer",
              outline: "none",
              fontSize: ".875rem",
              fontWeight: 600,
              color: "var(--team-app-heading-dark-navy)",
              "&:hover, &:focus-visible": {
                borderColor: "var(--team-app-interactive-active)",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedOption?.label}
            </Box>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="text-xs"
              style={{
                transition: "transform 120ms",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          </Box>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-[22rem] rounded-xl border-[#ECEEF9] bg-white p-2 shadow-[0_4px_30px_rgba(0,0,0,0.10)]"
        >
          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: ".5rem",
              height: "2.25rem",
              padding: "0 .625rem",
              marginBottom: ".375rem",
              border: "1px solid var(--team-app-filter-active-border)",
              borderRadius: ".5rem",
              "&:focus-within": {
                borderColor: "var(--team-app-interactive-active)",
              },
            }}
          >
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="text-xs"
              style={{ color: "#4C6283" }}
            />
            <Box
              component="input"
              type="text"
              role="combobox"
              aria-label="Search reports"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                activeId !== null ? getOptionId(activeId) : undefined
              }
              placeholder="Search reports"
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
              onKeyDown={handleSearchKeyDown}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: ".875rem",
                color: "#002154",
                "&::placeholder": { color: "#6B7A90" },
              }}
            />
          </Box>

          {sections.length === 0 && (
            <Box
              role="status"
              sx={{
                padding: "1rem .5rem",
                fontSize: ".875rem",
                color: "#4C6283",
                textAlign: "center",
              }}
            >
              No reports match “{query}”
            </Box>
          )}
          <Box
            id={listboxId}
            role="listbox"
            aria-label="Reports"
            sx={{ maxHeight: "18rem", overflowY: "auto" }}
          >
            {sections.map((section, index) => {
              const groupLabelId = `${baseId}-group-${index}`;
              return (
                <Box
                  key={`${section.group ?? "ungrouped"}-${index}`}
                  role={section.group ? "group" : "presentation"}
                  aria-labelledby={section.group ? groupLabelId : undefined}
                >
                  {index > 0 && (
                    <Box
                      aria-hidden
                      sx={{
                        height: "1px",
                        background: "#ECEEF9",
                        margin: ".375rem .25rem",
                      }}
                    />
                  )}
                  {section.group && (
                    <Box
                      id={groupLabelId}
                      role="presentation"
                      sx={{
                        padding: ".5rem .5rem .25rem",
                        fontSize: ".6875rem",
                        fontWeight: 600,
                        letterSpacing: ".05em",
                        textTransform: "uppercase",
                        color: "#4C6283",
                      }}
                    >
                      {section.group}
                    </Box>
                  )}
                  {section.options.map((option) => {
                    const isSelected = option.id === value;
                    const isActive = option.id === activeId;
                    return (
                      <Box
                        key={option.id}
                        id={getOptionId(option.id)}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelect(option.id)}
                        onMouseMove={() => setActiveId(option.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: ".5rem",
                          width: "100%",
                          minHeight: "2.5rem",
                          padding: `.5rem .625rem .5rem ${
                            section.group ? "1.125rem" : ".625rem"
                          }`,
                          boxSizing: "border-box",
                          border: "none",
                          borderRadius: ".5rem",
                          cursor: "pointer",
                          textAlign: "left",
                          fontSize: ".875rem",
                          lineHeight: "1.25rem",
                          fontWeight: isSelected ? 700 : 400,
                          color: isSelected ? "#214294" : "#002154",
                          background: isSelected
                            ? "#ECEEFB"
                            : isActive
                            ? "#F2F4F6"
                            : "transparent",
                          // The selected row is already shaded, so mark it with a ring when active
                          boxShadow:
                            isActive && isSelected
                              ? "inset 0 0 0 1px var(--team-app-interactive-active)"
                              : "none",
                        }}
                      >
                        <Box component="span" sx={{ flex: "1 1 auto" }}>
                          {option.label}
                        </Box>
                        {isSelected && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-sm"
                            style={{
                              color: "var(--team-app-interactive-active)",
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
